import React, { useCallback, useEffect, useState } from 'react';
import { TextField } from '@mui/material';

/**
 * Number-only text input that holds a free-form draft string while focused
 * and only re-syncs to the canonical model value on blur. Used for the
 * buy/shipping/sell fields on each QuoteOption in both the QuoteEditor and
 * the DesktopBuildEditor.
 *
 * The buy/shipping/sell columns store ex-GST internally; the input displays
 * in the quote's `priceDisplayMode` (inc or ex). The focus-aware draft avoids
 * the visual jitter that an unconditional `ex → display → ex` round trip
 * produces on every keystroke when the displayed value can't be represented
 * exactly in 2-decimal ex-GST.
 *
 * `type="text"` + `inputMode="decimal"` is used instead of `type="number"`
 * to suppress the browser's spinner arrows (techs type prices, never step
 * them) and to allow trailing dots / partial decimals while typing.
 */
export const PriceInput: React.FC<{
  label: string;
  exValue: number;
  priceDisplayMode: 'ex' | 'inc';
  onChange: (exValue: number) => void;
  disabled?: boolean;
  sx?: React.ComponentProps<typeof TextField>['sx'];
}> = ({ label, exValue, priceDisplayMode, onChange, disabled, sx }) => {
  const round2 = (n: number) => Math.round(n * 100) / 100;
  // Memoised only so the sync effect below can list it: its sole input is
  // `priceDisplayMode`, so the identity changes exactly when the conversion
  // does. Left unmemoised, listing it would re-run the effect every render.
  const toDisplay = useCallback(
    (ex: number) => (priceDisplayMode === 'inc' ? round2(ex * 1.1) : round2(ex)),
    [priceDisplayMode],
  );
  // On the inc→ex divide, keep full precision: rounding the ex value here
  // (e.g. 0.05 inc → round2(0.0454…) = 0.05 → redisplays as 0.06) leaks a
  // cent back into the inc display for ~9% of inputs. Storage stays ex-GST
  // and toDisplay() does the final round2, so the round-trip is stable.
  // Direct ex input divides cleanly, so round it normally.
  const fromDisplay = (displayed: number) =>
    priceDisplayMode === 'inc' ? displayed / 1.1 : round2(displayed);

  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState<string>(() => {
    const d = toDisplay(exValue);
    return d === 0 ? '' : String(d);
  });

  // While unfocused, mirror external model changes (e.g. picked from
  // inventory, regenerated total). Skipped while typing so we don't
  // stomp on the user's in-flight draft.
  useEffect(() => {
    if (focused) return;
    const d = toDisplay(exValue);
    setDraft(d === 0 ? '' : String(d));
  }, [exValue, toDisplay, focused]);

  return (
    <TextField
      label={label}
      type="text"
      inputMode="decimal"
      size="small"
      value={draft}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        const v = e.target.value;
        setDraft(v);
        if (v.trim() === '') {
          onChange(0);
          return;
        }
        const parsed = Number(v);
        if (Number.isFinite(parsed)) onChange(fromDisplay(parsed));
      }}
      disabled={disabled}
      sx={sx}
    />
  );
};
